import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { cleanClassification } from './ClassificationCombobox';

type Row = { kind: 'option'; value: string } | { kind: 'create'; value: string };

/**
 * Inline multi-select for list classification fields (vertical). Pick any
 * number of existing values; a value not in `options` needs an explicit
 * "Create" row, offered to admins only (`canCreate`) — the server enforces the
 * same rule and snaps case variants to the existing spelling.
 */
export default function MultiValueCombobox({
  value,
  options,
  canCreate,
  saving,
  onCommit,
  onCancel,
}: {
  value: string[];
  options: string[];
  canCreate: boolean;
  saving?: boolean;
  /** Called with the chosen values; [] clears the field. */
  onCommit: (values: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(value);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const has = (v: string) => selected.some((s) => s.toLowerCase() === v.toLowerCase());

  const rows: Row[] = useMemo(() => {
    const q = cleanClassification(query).toLowerCase();
    const matches = options
      .filter((o) => !q || o.toLowerCase().includes(q))
      .sort((a, b) => {
        const pa = a.toLowerCase().startsWith(q) ? 0 : 1;
        const pb = b.toLowerCase().startsWith(q) ? 0 : 1;
        return pa - pb || a.localeCompare(b);
      })
      .map((o): Row => ({ kind: 'option', value: o }));
    const exists = !!q && (options.some((o) => o.toLowerCase() === q) || selected.some((s) => s.toLowerCase() === q));
    return q && !exists && canCreate ? [...matches, { kind: 'create', value: cleanClassification(query) }] : matches;
  }, [query, options, selected, canCreate]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) onCancel(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancel]);

  const toggle = (row: Row | undefined) => {
    if (!row || saving) return;
    setSelected((s) => (has(row.value) ? s.filter((x) => x.toLowerCase() !== row.value.toLowerCase()) : [...s, row.value]));
    setQuery('');
    inputRef.current?.focus();
  };

  const save = () => { if (!saving) onCommit(selected); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter toggles the highlighted row; Shift/Cmd/Ctrl+Enter (or Enter on an empty search) saves.
      if (e.shiftKey || e.metaKey || e.ctrlKey || (!query.trim() && rows.length === 0)) save();
      else toggle(rows[active]);
    }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    else if (e.key === 'Backspace' && !query && selected.length) setSelected((s) => s.slice(0, -1));
  };

  return (
    <div ref={boxRef} className="relative flex-1">
      <div className="flex items-start gap-1.5">
        <div className="input-sm flex min-h-[2rem] flex-1 cursor-text flex-wrap items-center gap-1 py-1" onClick={() => inputRef.current?.focus()}>
          {selected.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-700">
              {v}
              <button type="button" disabled={saving} onClick={(e) => { e.stopPropagation(); setSelected((s) => s.filter((x) => x !== v)); }}
                className="text-violet-400 hover:text-violet-700" title={`Remove ${v}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={selected.length ? 'Add…' : 'Search…'}
            className="min-w-[6rem] flex-1 border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0"
            disabled={saving}
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
          />
        </div>
        <button onClick={save} disabled={saving} className="btn btn-primary px-2 py-1 text-xs" title="Save (Shift+Enter)">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="btn-ghost mt-1 text-slate-400 hover:text-slate-600" title="Cancel">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div ref={listRef} role="listbox" aria-multiselectable="true"
        className="absolute left-0 right-24 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-surface py-1 shadow-lg">
        {rows.length === 0 && (
          <div className="px-3 py-2 text-xs text-slate-400">
            {query.trim() ? 'No matching value. Only admins can add new values.' : 'No values yet.'}
          </div>
        )}
        {rows.map((row, idx) => {
          const isActive = idx === active;
          const base = `w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${isActive ? 'bg-brand-50' : ''}`;
          if (row.kind === 'create') {
            return (
              <button key="__create" data-idx={idx} type="button" role="option" aria-selected={false}
                onMouseEnter={() => setActive(idx)} onClick={() => toggle(row)} className={`${base} border-t border-slate-100 text-brand-700`}>
                <Plus className="h-3.5 w-3.5 shrink-0" /> Create “{row.value}”
              </button>
            );
          }
          const on = has(row.value);
          return (
            <button key={row.value} data-idx={idx} type="button" role="option" aria-selected={on}
              onMouseEnter={() => setActive(idx)} onClick={() => toggle(row)}
              className={`${base} ${on ? 'font-medium text-brand-700' : 'text-slate-700'}`}>
              <span className="flex-1 truncate">{row.value}</span>
              {on && <Check className="h-3.5 w-3.5 shrink-0 text-brand-600" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
